// OWNER: FRONTEND-ADMIN. Batch runners on the campaign page (SPEC §15.5):
// - "Run lookups": Twilio Line Type Intelligence, $0.008 each. Shows the cost before starting and never runs more than
//   the number the admin confirmed.
// - "Run briefs": AI pre-call briefs, 10 per request, hidden when AI isn't configured.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { LookupRunResult } from '../../../src/shared/api-core';
import type { BriefsRunResponse } from '../../../src/shared/api-ai';
import { errorMessage } from '../api';
import { ErrorBanner } from '../components/ui';
import { adminApi } from './endpoints';
import { ConfirmModal, Field, Progress, Stat, inputValue, money, useLoad } from './ui';

export const LOOKUP_PRICE_USD = 0.008;   // Twilio Lookup v2 line_type_intelligence, per request (build-cost.md §2.5)
const LOOKUP_BATCH = 25;                 // SPEC §7.4: ≤ 25 per request
const BRIEF_BATCH = 10;                  // SPEC §13.1: limit ≤ 10

type Failure = { businessId: number; name: string; error: string };

export function LookupRunner(props: { campaignId: number }) {
  const pending = useLoad(() => adminApi.lookupsPending(props.campaignId), [props.campaignId]);
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [target, setTarget] = useState(0);
  const [processed, setProcessed] = useState(0);
  const [updated, setUpdated] = useState(0);
  const [cost, setCost] = useState(0);
  const [failures, setFailures] = useState<Failure[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState<string | null>(null);
  const stop = useRef(false);
  useEffect(() => () => { stop.current = true; }, []);

  const n = pending.data?.pending ?? 0;
  const estimate = n * LOOKUP_PRICE_USD;

  const run = async () => {
    const cap = n; // never run more than the confirmed count, even if more become due meanwhile
    stop.current = false;
    setRunning(true); setTarget(cap); setProcessed(0); setUpdated(0); setCost(0); setFailures([]); setError(null); setFinished(null);
    let done = 0;
    let fullyFailedBatches = 0;
    try {
      while (done < cap && !stop.current) {
        const limit = Math.min(LOOKUP_BATCH, cap - done);
        const r: LookupRunResult = await adminApi.lookupsRun({ campaignId: props.campaignId, limit });
        done += r.processed;
        setProcessed(done);
        setUpdated((u) => u + r.updated);
        setCost((c) => c + (r.costUsd ?? 0));
        if (r.failed?.length) setFailures((f) => [...f.filter((x) => !r.failed.some((y) => y.businessId === x.businessId)), ...r.failed]);
        if (r.processed === 0 || r.remaining === 0) break;
        fullyFailedBatches = r.failed?.length === r.processed ? fullyFailedBatches + 1 : 0;
        if (fullyFailedBatches >= 2) { setError('Two batches in a row failed completely, so the run stopped. Check the errors below (Twilio setup or Canadian lookup approval).'); break; }
      }
      setFinished(stop.current ? `Stopped after ${done} lookups.` : `Finished: ${done} lookups.`);
    } catch (e) {
      setError(`${errorMessage(e)} (stopped after ${done} lookups)`);
    } finally {
      setRunning(false);
      void pending.reload();
    }
  };

  return (
    <div class="card stack">
      <h3>Line-type lookups</h3>
      <div class="adm-sub">
        Twilio Lookup tells us whether each US number is a landline, mobile or VoIP line. Mobile and unknown numbers need a national
        DNC scrub before they can be dialled, and voicemail drops need a fresh landline result. Lookups older than 14 days are redone.
      </div>
      <ErrorBanner error={pending.error} />
      {pending.data ? (
        <div class="row" style="flex-wrap:wrap;gap:10px">
          <Stat label="numbers need a lookup" value={n.toLocaleString()} />
          <Stat label={`estimate at ${money(LOOKUP_PRICE_USD)} each`} value={money(estimate)} />
        </div>
      ) : null}
      {running || processed > 0 ? (
        <div class="stack">
          <Progress done={processed} total={target} />
          <div class="adm-sub">{processed} of {target} looked up · {updated} updated · {failures.length} failed · {money(cost)} billed so far (per the server)</div>
        </div>
      ) : null}
      <ErrorBanner error={error} />
      {finished ? <div class="chip chip-ok" style="display:block;padding:8px 10px">{finished}</div> : null}
      <div class="row">
        {running ? (
          <button class="btn" onClick={() => { stop.current = true; }}>Stop after this batch</button>
        ) : (
          <button class="btn btn-ink" disabled={!pending.data || n === 0} onClick={() => setConfirming(true)}>
            {n === 0 ? 'Nothing to look up' : `Run ${n.toLocaleString()} lookups…`}
          </button>
        )}
      </div>
      {failures.length ? (
        <div class="adm-table-wrap" style="max-height:240px">
          <table>
            <thead><tr><th>Business</th><th>Error</th></tr></thead>
            <tbody>{failures.map((f) => <tr><td>{f.name}</td><td>{f.error}</td></tr>)}</tbody>
          </table>
        </div>
      ) : null}
      {confirming ? (
        <ConfirmModal
          title="Run Twilio lookups?"
          body={<div class="stack">
            <div>This runs <b>{n.toLocaleString()}</b> Twilio line-type lookups at <b>{money(LOOKUP_PRICE_USD)}</b> each.</div>
            <div style="font-size:18px">Estimated cost: <b>{money(estimate)}</b> (billed to the Twilio account).</div>
            <div class="adm-sub">The run stops at {n.toLocaleString()} even if more numbers become due while it runs. You can stop it between batches.</div>
          </div>}
          confirmLabel={`Yes, run ${n.toLocaleString()} lookups (${money(estimate)})`}
          onConfirm={async () => { void run(); }}
          onClose={() => setConfirming(false)}
        />
      ) : null}
    </div>
  );
}

export function BriefRunner(props: { campaignId: number }) {
  const status = useLoad(() => adminApi.aiStatus(), []);
  const [max, setMax] = useState(50);
  const [running, setRunning] = useState(false);
  const [processed, setProcessed] = useState(0);
  const [ok, setOk] = useState(0);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [failures, setFailures] = useState<BriefsRunResponse['failed']>([]);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState<string | null>(null);
  const stop = useRef(false);
  useEffect(() => () => { stop.current = true; }, []);

  if (status.error) return <div class="card stack"><h3>AI pre-call briefs</h3><ErrorBanner error={status.error} /></div>;
  if (!status.data) return null;
  if (!status.data.configured) {
    return (
      <div class="card stack">
        <h3>AI pre-call briefs</h3>
        <div class="adm-muted-box">{status.data.message ?? 'AI briefs are off: the Anthropic API key is not set on the server.'}</div>
      </div>
    );
  }

  const run = async () => {
    const cap = Math.max(1, Math.floor(max));
    stop.current = false;
    setRunning(true); setProcessed(0); setOk(0); setFailures([]); setError(null); setFinished(null);
    let done = 0;
    try {
      while (done < cap && !stop.current) {
        const r = await adminApi.briefsRun({ campaignId: props.campaignId, limit: Math.min(BRIEF_BATCH, cap - done) });
        done += r.processed;
        setProcessed(done);
        setOk((x) => x + r.ok);
        setRemaining(r.remaining);
        // the server re-picks businesses that failed (they still have no brief), so keep one row per business
        if (r.failed?.length) setFailures((f) => [...f.filter((x) => !r.failed.some((y) => y.businessId === x.businessId)), ...r.failed]);
        if (r.stoppedReason) { setError(`Stopped: ${r.stoppedReason}`); break; }
        if (r.processed === 0 || r.remaining === 0) break;
        if (r.ok === 0) { setError('Nothing in the last batch could be briefed (see below), so the run stopped rather than retrying the same businesses.'); break; }
      }
      setFinished(stop.current ? `Stopped after ${done} briefs.` : `Finished: ${done} businesses researched.`);
    } catch (e) {
      setError(`${errorMessage(e)} (stopped after ${done})`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div class="card stack">
      <h3>AI pre-call briefs</h3>
      <div class="adm-sub">
        Reads each business's website{status.data.places ? ' and Google profile' : ''} and writes a short brief with sourced facts only.
        Observations stay unverified until a setter checks the source and clicks Verify.
      </div>
      <div class="row" style="align-items:flex-end">
        <Field label="How many businesses (max)"><input type="number" min={1} max={1000} value={max} disabled={running} onInput={(e) => setMax(Number(inputValue(e)) || 1)} style="width:120px" /></Field>
        {running
          ? <button class="btn" onClick={() => { stop.current = true; }}>Stop after this batch</button>
          : <button class="btn btn-ink" onClick={run}>Run briefs</button>}
      </div>
      {running || processed > 0 ? (
        <div class="stack">
          <Progress done={processed} total={Math.max(1, Math.min(max, processed + (remaining ?? max)))} />
          <div class="adm-sub">{processed} processed · {ok} briefed · {failures.length} failed{remaining !== null ? ` · ${remaining} still without a fresh brief` : ''}</div>
        </div>
      ) : null}
      <ErrorBanner error={error} />
      {finished ? <div class="chip chip-ok" style="display:block;padding:8px 10px">{finished}</div> : null}
      {failures.length ? (
        <div class="adm-table-wrap" style="max-height:240px">
          <table><thead><tr><th>Business</th><th>Why it failed</th></tr></thead>
            <tbody>{failures.map((f) => <tr><td>{f.name}</td><td>{f.message}</td></tr>)}</tbody></table>
        </div>
      ) : null}
    </div>
  );
}
